import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { SectionContent, SectionHeading } from "../shared/Section";
import splitText from "../shared/splitText";
import { camerasPositions } from "../shared/three/Camera";
import useStore from "../shared/useStore";
import Card from "./Card";

const Content = styled.div`
  opacity: ${(props) => (props.isOpen ? 0 : 1)} !important;
  pointer-events: ${(props) => (props.isOpen ? "none" : "auto")};
  transition: 400ms;
`;

const ProjectsWrapper = styled.div`
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  transition: 400ms;
  position: absolute;
  top: 10vh;
  display: flex;
  height: 90vh;
  width: 100%;
  padding: 5vh 5vw;
  @media (${(props) => props.theme.breakpoint}) {
    height: 85vh;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  & h2 {
    width: 250px;
    margin-bottom: 0;
    margin-right: 1rem;
  }
  @media (${(props) => props.theme.breakpoint}) {
    flex-direction: column;
    align-items: flex-start;
    h2 {
      width: auto;
      margin-right: 0;
      margin-bottom: 1.5rem;
    }
  }
`;

const ProjectList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  list-style: none;
`;

const PeopleList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.6rem;
  list-style: none;
  width: 210px;
  @media (${(props) => props.theme.breakpoint}) {
    width: auto;
    margin-bottom: 1.5rem;
  }
`;

const People = styled.span`
  opacity: 0.7;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
`;

const Project = styled.button`
  display: flex;
  opacity: 0.7;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  background-color: transparent;
  border: 0;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;

const ProjectNumber = styled.span`
  display: inline-block;
  width: 2.5rem;
  text-align: left;
`;

const initialCameraPosition = {
  x: camerasPositions[2].position.x,
  y: camerasPositions[2].position.y,
  z: camerasPositions[2].position.z,
};

const zoomedCameraPosition = {
  x: initialCameraPosition.x - 1,
  y: initialCameraPosition.y,
  z: initialCameraPosition.z - 2,
};

const Projects = ({ visible }) => {
  const { t } = useTranslation();

  const topHeading = useRef();
  const bottomHeading = useRef();

  const activeProject = useStore((state) => state.activeProject);
  const updateActiveProject = useStore((state) => state.updateActiveProject);
  const updateCameraPosition = useStore((state) => state.updateCameraPosition);

  const [currentProject, setStateProject] = useState(activeProject);

  const setCurrentProject = (index) => {
    setStateProject(index);
    updateActiveProject(index);
  };

  const projects = [
    {
      title: "CSS Blog",
      description: t("Project.001"),
      link: "https://blog.dorianguilmain.com/",
      image: "/pictures/001.png",
      onClick: () => {
        updateCameraPosition(zoomedCameraPosition);
        setCurrentProject(0);
      },
    },
    {
      title: "CSS Only Codepen",
      description: t("Project.002"),
      link: "https://codepen.io/Craaftx/",
      image: "/pictures/002.jpg",
      onClick: () => {
        updateCameraPosition(zoomedCameraPosition);
        setCurrentProject(1);
      },
    },
  ];

  useEffect(() => {
    if (topHeading && bottomHeading) {
      splitText(topHeading.current);
      splitText(bottomHeading.current);
    }
  }, [topHeading, bottomHeading]);

  useEffect(() => {
    const unsubscribe = useStore.subscribe(
      (currentSection) => {
        if (currentSection !== 2) {
          setStateProject(null);
          updateActiveProject(null);
        }
      },
      (state) => state.currentSection
    );
    return () => {
      unsubscribe();
    };
  }, [updateActiveProject]);

  return (
    <>
      <SectionContent
        className={
          visible ? "section-content skills active" : "section-content skills"
        }
      >
        <Content isOpen={currentProject !== null}>
          <Row>
            <SectionHeading textAlign="right" as={"h2"} ref={topHeading}>
              {t("Projects.firstTitle")}
            </SectionHeading>
            <PeopleList>
              {[
                "Alsacreations",
                "Storyfox",
                "Jesuisundev",
                "Becoms",
                "Cikaba",
              ].map((name, index) => (
                <People key={`${index}-${name}`}>{name}</People>
              ))}
            </PeopleList>
          </Row>

          <Row>
            <SectionHeading textAlign="right" as={"h2"} ref={bottomHeading}>
              <Trans i18nKey={"Projects.secondTitle"}>
                Building <strong>creative</strong> project
              </Trans>
            </SectionHeading>
            <ProjectList>
              {projects.map((p, index) => (
                <li key={index}>
                  <Project
                    onClick={() => {
                      setCurrentProject(index);
                      p.onClick();
                    }}
                  >
                    <ProjectNumber>{`00${index + 1}`}</ProjectNumber>
                    {p.title}
                  </Project>
                </li>
              ))}
            </ProjectList>
          </Row>
        </Content>
      </SectionContent>
      <ProjectsWrapper isOpen={currentProject !== null}>
        {projects.map((p, index) => (
          <Card
            key={index}
            project={p}
            isOpen={currentProject === index}
            onClose={() => {
              setCurrentProject(null);
              updateCameraPosition(initialCameraPosition);
            }}
          />
        ))}
      </ProjectsWrapper>
    </>
  );
};

export default Projects;
